import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Rocket, AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Bookmarkable demo URL — visit /demo and you land on a fresh, isolated
 * Demo Dealership workshop with the Jane Driver inspection pre-loaded.
 *
 * No clicks, no forms, no clutter. Used for live dealer pitches.
 */
export default function LaunchDemoPage() {
  const { launchDemoTenant } = useAuth();
  const nav = useNavigate();
  const [error, setError] = useState(null);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    (async () => {
      try {
        await launchDemoTenant();
        // Tiny pause so the loader animation feels intentional, not jittery.
        setTimeout(() => nav("/dashboard", { replace: true }), 350);
      } catch (e) {
        setError(e?.response?.data?.detail || "We couldn't spin up the demo. Please try again in a moment.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-background grid place-items-center p-6" data-testid="launch-demo-page">
      <div className="card-default p-8 max-w-md w-full text-center">
        {!error ? (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/15 grid place-items-center">
              <Rocket className="w-7 h-7 text-primary" />
            </div>
            <h1 className="font-display font-black text-2xl tracking-tight mt-5">
              Spinning up your workshop
            </h1>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              We're staging a fresh Demo Dealership with the Jane Driver inspection — it'll be ready in a few seconds.
            </p>
            <div className="mt-6 flex items-center justify-center gap-2 text-primary">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm font-mono-plex">stand by…</span>
            </div>
          </>
        ) : (
          <>
            <div className="w-16 h-16 mx-auto rounded-full bg-destructive/15 grid place-items-center">
              <AlertTriangle className="w-7 h-7 text-destructive" />
            </div>
            <h1 className="font-display font-black text-2xl tracking-tight mt-5">
              We hit a snag
            </h1>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed" data-testid="launch-demo-error">
              {error}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-primary text-sm mt-5"
              data-testid="launch-demo-retry-btn"
            >
              <Rocket className="w-4 h-4" /> Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
